import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Ticket, Wallet, History, Bell, User, HelpCircle, LogOut, ChevronLeft, ChevronRight, Menu, X, Gift, LayoutGrid, Trophy, Radio } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: Home },
  { key: "tokens", label: "Check Token", icon: Ticket },
  { key: "wallet", label: "Wallet", icon: Wallet },
  { key: "lucky", label: "My Lucky Tickets", icon: Trophy },
  { key: "live", label: "Live Board", icon: Radio },
  { key: "referral", label: "Refer & Earn", icon: Gift },
  { key: "transactions", label: "Transactions", icon: History },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "profile", label: "Profile", icon: User },
  { key: "help", label: "Help & Support", icon: HelpCircle },
];

const MORE = [
  { key: "lucky", label: "My Lucky Tickets", icon: Trophy },
  { key: "live", label: "Live Board", icon: Radio },
  { key: "referral", label: "Refer & Earn", icon: Gift },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "profile", label: "Profile", icon: User },
  { key: "help", label: "Help & Support", icon: HelpCircle },
];

const TITLES = {
  dashboard: "Dashboard", tokens: "Check Token", wallet: "Wallet", lucky: "My Lucky Tickets",
  live: "Live Board",
  transactions: "Transactions", notifications: "Notifications",
  profile: "Profile", help: "Help & Support", redeem: "Redeem Cashback",
};

export default function CustomerLayout({ active, onNav, unread = 0, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const doLogout = async () => { await logout(); navigate("/"); };
  const nav = (k) => { setMoreOpen(false); onNav(k); };

  return (
    <div className="cx-theme min-h-screen bg-slate-950 text-slate-100">
      <header className="glass sticky top-0 z-40 border-b border-slate-800">
        <div className="relative flex items-center justify-between px-4 py-3 md:px-6">
          <div className="flex items-center gap-2.5">
            <button data-testid="header-hamburger" onClick={() => setMoreOpen(true)} aria-label="Menu" className="rounded-lg p-1.5 text-slate-200 md:hidden"><Menu className="h-6 w-6" /></button>
            <div className="flex items-center gap-2.5 md:relative md:left-0 max-md:absolute max-md:left-1/2 max-md:-translate-x-1/2">
              <img src="/paras-logo.png" alt="Paras CashBack" className="h-9 w-9 object-contain" />
              <div className="leading-none">
                <p className="font-heading text-sm font-bold">Paras CashBack</p>
                <p className="mt-0.5 hidden font-mono text-[10px] uppercase tracking-widest text-indigo-300 sm:block">{TITLES[active] || "Dashboard"}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="notif-bell" onClick={() => onNav("notifications")} aria-label="Notifications" className="relative rounded-full border border-slate-700 p-2 text-slate-300 transition hover:border-indigo-400/60 hover:text-white">
              <Bell className="h-4 w-4" />
              {unread > 0 && <span data-testid="notif-unread-dot" className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-amber-500 px-1 text-[9px] font-bold text-slate-950">{unread > 9 ? "9+" : unread}</span>}
            </button>
            <button data-testid="header-avatar" onClick={() => onNav("profile")} aria-label="Profile" className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 font-heading text-sm font-bold text-white">
              {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl">
        <aside data-testid="desktop-sidebar" className={`sticky top-[61px] hidden h-[calc(100vh-61px)] flex-shrink-0 flex-col border-r border-slate-800 p-3 transition-all duration-200 md:flex ${collapsed ? "w-16" : "w-60"}`}>
          <nav className="flex-1 space-y-1">
            {ITEMS.map((it) => {
              const A = active === it.key || (it.key === "wallet" && active === "redeem");
              const I = it.icon;
              return (
                <button key={it.key} data-testid={`sidebar-${it.key}`} onClick={() => onNav(it.key)} title={it.label}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${A ? "bg-indigo-500/20 text-indigo-200" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                  <I className="h-5 w-5 flex-shrink-0" />
                  {!collapsed && <span className="flex-1 text-left">{it.label}</span>}
                  {!collapsed && it.key === "notifications" && unread > 0 && <span className="rounded-full bg-emerald-500 px-1.5 text-[10px] font-bold text-slate-950">{unread}</span>}
                </button>
              );
            })}
          </nav>
          <button data-testid="sidebar-logout" onClick={doLogout} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-slate-400 transition hover:bg-rose-500/10 hover:text-rose-300">
            <LogOut className="h-5 w-5 flex-shrink-0" />{!collapsed && "Logout"}
          </button>
          <button data-testid="sidebar-collapse" onClick={() => setCollapsed((v) => !v)} aria-label="Collapse sidebar" className="mt-1 flex items-center justify-center rounded-xl px-3 py-2 text-slate-500 transition hover:text-white">
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </aside>

        <main className="min-w-0 flex-1 px-4 py-6 pb-28 md:px-8 md:pb-10">{children}</main>
      </div>

      {/* mobile bottom nav with center FAB */}
      <nav data-testid="customer-bottom-nav" className="glass fixed inset-x-0 bottom-0 z-50 border-t border-slate-800 pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="relative mx-auto grid max-w-md grid-cols-5 items-end">
          {[{ key: "dashboard", label: "Home", icon: Home }, { key: "wallet", label: "Wallet", icon: Wallet }].map((it) => {
            const A = active === it.key || (it.key === "wallet" && active === "redeem"); const I = it.icon;
            return (
              <button key={it.key} data-testid={`bottomnav-${it.key}`} onClick={() => onNav(it.key)} className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${A ? "text-indigo-300" : "text-slate-500"}`}>
                <I className="h-5 w-5" />{it.label}
              </button>
            );
          })}
          <div className="flex justify-center">
            <button data-testid="bottomnav-check-cashback" onClick={() => onNav("tokens")} className="cx-hero -mt-6 grid h-16 w-16 place-items-center rounded-full border-4 border-slate-950 text-center text-[11px] font-bold leading-tight text-white shadow-lg">
              Check<br />Cashback
            </button>
          </div>
          {[{ key: "transactions", label: "History", icon: History }, { key: "more", label: "More", icon: LayoutGrid }].map((it) => {
            const A = it.key === "more" ? moreOpen : active === it.key;
            const I = it.icon;
            return (
              <button key={it.key} data-testid={`bottomnav-${it.key}`} onClick={() => it.key === "more" ? setMoreOpen(true) : onNav(it.key)} className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${A ? "text-indigo-300" : "text-slate-500"}`}>
                <I className="h-5 w-5" />{it.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* More bottom sheet */}
      {moreOpen && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="more-sheet">
          <div className="absolute inset-0 bg-slate-950/70" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 rounded-t-3xl border-t border-slate-800 bg-slate-900 p-5 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-heading text-lg font-bold">More</p>
              <button onClick={() => setMoreOpen(false)} aria-label="Close" className="rounded-lg p-1.5 text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {MORE.map((it) => {
                const I = it.icon;
                return (
                  <button key={it.key} data-testid={`more-${it.key}`} onClick={() => nav(it.key)} className="flex items-center gap-3 rounded-2xl border border-slate-800 p-4 text-left hover:border-indigo-400/50">
                    <I className="h-5 w-5 text-indigo-300" />
                    <span className="text-sm font-semibold">{it.label}</span>
                    {it.key === "notifications" && unread > 0 && <span className="ml-auto rounded-full bg-emerald-500 px-1.5 text-[10px] font-bold text-slate-950">{unread}</span>}
                  </button>
                );
              })}
            </div>
            <button data-testid="more-logout" onClick={doLogout} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-500/40 p-3 text-sm font-semibold text-rose-300 hover:bg-rose-500/10">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
